import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../../auth/auth';
import SignInModal from '../../auth/SignInModal/SignInModal';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { generateClient } from 'aws-amplify/api';
import * as queries from '../../graphQL/queries';
import './Header.css';
import { hasUnreadMessages as hasUnreadMessagesFor } from '../../utils/unreadMessages';
import { trackEvent } from '../../utils/analytics';
import { mediaUrl } from '../../utils/media';
import { getSafeRedirectPath } from '../../utils/safeRedirect';
import { initialsAvatar } from '../../utils/initialsAvatar';

const Header = () => {
  const { user, userType, SignOut, userID } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = getSafeRedirectPath(location.state?.from);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const openedWithKeyboardRef = useRef(false);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [profilePictureKey, setProfilePictureKey] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [hasUnreadMessages, setHasUnreadMessages] = useState(false);
  const [hasUnreadInvoices, setHasUnreadInvoices] = useState(false);
  const [hasPendingRequests, setHasPendingRequests] = useState(false);
  const client = useMemo(() => generateClient(), []);

  useEffect(() => {
    if (user) {
      fetchProfilePicture();
      fetchUnreadMessages();
      fetchUnreadInvoices();
      fetchPendingRequests();
      setShowSignInModal(false);

      const interval = setInterval(() => {
        fetchUnreadMessages();
        fetchUnreadInvoices();
        fetchPendingRequests();
      }, 60000);

      const handleVisibility = () => {
        if (document.visibilityState === 'visible') {
          fetchUnreadMessages();
          fetchUnreadInvoices();
          fetchPendingRequests();
        }
      };
      const handleRequestsUpdated = () => fetchPendingRequests();
      const handleMessagesRead = () => fetchUnreadMessages();
      document.addEventListener('visibilitychange', handleVisibility);
      window.addEventListener('requestsUpdated', handleRequestsUpdated);
      window.addEventListener('messagesRead', handleMessagesRead);

      return () => {
        clearInterval(interval);
        document.removeEventListener('visibilitychange', handleVisibility);
        window.removeEventListener('requestsUpdated', handleRequestsUpdated);
        window.removeEventListener('messagesRead', handleMessagesRead);
      };
    }
  }, [user]);

  useEffect(() => {
    if (!user && returnTo) setShowSignInModal(true);
  }, [user, returnTo]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDropdown && !event.target.closest('.profile-button')) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showDropdown]);

  const fetchProfilePicture = async () => {
    try {
      let profileKey = '';
      if (userType === 'Company') {
        const { data } = await client.graphql({
          query: queries.getCompany,
          variables: { id: userID },
        });
        profileKey = data.getCompany.profilepicturekey || '';
      } else {
        const { data } = await client.graphql({
          query: queries.getUser,
          variables: { id: userID },
        });
        profileKey = data.getUser.profilepicturekey || '';
      }
      setProfilePictureKey(profileKey);
    } catch (error) {
      setProfilePictureKey('');
    }
  };

  const fetchUnreadMessages = async () => {
    try {
      setHasUnreadMessages(await hasUnreadMessagesFor(client, userID));
    } catch (error) {
      setHasUnreadMessages(false);
    }
  };

  const fetchUnreadInvoices = async () => {
    try {
      const result = await client.graphql({
        query: queries.invoicesByUserID,
        variables: { userID: userID },
      });
      const unreadInvoices = result.data.invoicesByUserID.items.some(invoice => !invoice.readStatus);
      setHasUnreadInvoices(unreadInvoices);
    } catch (error) {
      setHasUnreadInvoices(false);
    }
  };

  const fetchPendingRequests = async () => {
    try {
      const result = await client.graphql({
        query: queries.requestsByCompanyID,
        variables: { companyID: userID, filter: { isPending: { eq: true } } },
      });
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const pendingRequests = result.data.requestsByCompanyID.items.filter(
        r => new Date(r.requestDate + 'T00:00:00') >= today
      );
      setHasPendingRequests(pendingRequests.length > 0);
    } catch (error) {
      setHasPendingRequests(false);
    }
  };

  const toggleSignInModal = () => {
    setShowSignInModal(!showSignInModal);
  };

  const toggleDropdown = (e) => {
    e.stopPropagation();
    openedWithKeyboardRef.current = e.detail === 0;
    setShowDropdown(!showDropdown);
  };

  const getMenuItems = () => (menuRef.current ? Array.from(menuRef.current.querySelectorAll('[role="menuitem"]')) : []);

  const focusMenuItem = (index) => {
    const items = getMenuItems();
    if (!items.length) return;
    items[(index + items.length) % items.length].focus();
  };

  const closeDropdown = (returnFocus) => {
    setShowDropdown(false);
    if (returnFocus && triggerRef.current) triggerRef.current.focus();
  };

  useEffect(() => {
    if (!showDropdown || !openedWithKeyboardRef.current || !menuRef.current) return;
    const firstItem = menuRef.current.querySelector('[role="menuitem"]');
    if (firstItem) firstItem.focus();
  }, [showDropdown]);

  const handleTriggerKeyDown = (e) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      openedWithKeyboardRef.current = true;
      if (showDropdown) {
        focusMenuItem(e.key === 'ArrowDown' ? 0 : -1);
      } else {
        setShowDropdown(true);
      }
    } else if (e.key === 'Escape' && showDropdown) {
      e.preventDefault();
      closeDropdown(true);
    }
  };

  const handleMenuKeyDown = (e) => {
    const items = getMenuItems();
    const currentIndex = items.indexOf(document.activeElement);
    if (e.key === 'Escape') {
      e.preventDefault();
      closeDropdown(true);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      focusMenuItem(currentIndex + 1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      focusMenuItem(currentIndex - 1);
    } else if (e.key === 'Home') {
      e.preventDefault();
      focusMenuItem(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      focusMenuItem(-1);
    } else if (e.key === 'Tab') {
      closeDropdown(false);
    }
  };

  const canonicalUrl = `${window.location.origin}${location.pathname}`;
  const hasNewActivity = hasUnreadMessages || hasUnreadInvoices || hasPendingRequests;

  const goToHome = () => {
    navigate('/');
  };

  const handleLogout = async () => {
    trackEvent('click', { metadata: { target: 'nav_logout' } });
    await SignOut();
    navigate('/');
    setShowDropdown(false);
  };

  const handleGoToProfile = () => {
    trackEvent('click', { metadata: { target: 'nav_profile' } });
    const profilePath = userType === 'Company'
      ? `/contractor/${user.companyUser}`
      : `/user/${user.user}`;
    navigate(profilePath);
    setShowDropdown(false);
  };

  const handleGoToInvoices = () => {
    trackEvent('click', { metadata: { target: 'nav_invoices' } });
    const invoicePath = userType === 'Company'
      ? `/contractor/${user.companyUser}/spiggl-pay`
      : `/user/${user.user}/spiggl-pay`;
    navigate(invoicePath);
    setShowDropdown(false);
  };

  const handleGoToMessages = () => {
    trackEvent('click', { metadata: { target: 'nav_messages' } });
    navigate('/messages');
    setShowDropdown(false);
  };

  const handleCalendarClick = () => {
    navigate(`/contractor/${user.companyUser}/calendar`);
    setShowDropdown(false);
  };

  const profileImageSrc = profilePictureKey
  ? profilePictureKey.startsWith('default:')
    ? `/avatars/${profilePictureKey.split(':')[1]}.png`
    : mediaUrl(profilePictureKey)
  : userType === 'Company'
    ? initialsAvatar(user?.companyUser)
    : initialsAvatar(user?.user);

  return (
    <header className="Header">
      <Helmet titleTemplate="%s | Spiggl" defaultTitle="Spiggl — Find and Review Local Contractors">
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:url" content={canonicalUrl} />
      </Helmet>
      <div className="header-width">
        <a className="brand" href="/" onClick={(e) => { e.preventDefault(); goToHome(); }} aria-label="Spiggl home">
          <img src='/favicon.png' alt="Spiggl" className="header-logo" />
        </a>
        <nav className="user-nav">
          {user ? (
            <div className="profile-button">
              <button
                type="button"
                ref={triggerRef}
                className="profile-trigger"
                aria-haspopup="menu"
                aria-expanded={showDropdown}
                aria-controls="header-account-menu"
                aria-label={hasNewActivity ? 'Account menu, new activity' : 'Account menu'}
                onClick={toggleDropdown}
                onKeyDown={handleTriggerKeyDown}
              >
                <img
                  src={profileImageSrc}
                  alt=""
                  className="profile-picture"
                />
              </button>
              {hasNewActivity && (
                <div className="header-unread-indicator" aria-hidden="true" />
              )}
              {showDropdown && (
                <div className="header-dropdown-menu" id="header-account-menu" role="menu" aria-label="Account" ref={menuRef} onKeyDown={handleMenuKeyDown}>
                  <button type="button" role="menuitem" className="dropdown-profile" onClick={handleGoToProfile}>
                    <img
                      src={profileImageSrc}
                      alt=""
                      className="dropdown-profile-picture"
                    />
                    <span className="dropdown-profile-text">
                      <span>View Profile</span>
                      <span className="dropdown-username">{userType === 'Company' ? user.companyUser : user.user}</span>
                    </span>
                  </button>
                  <ul role="none">
                    <li role="none">
                      <button type="button" role="menuitem" className="header-menu-item" onClick={handleGoToInvoices}>
                        <img src="/icons/invoice.png" alt="" />
                        Spiggl Pay
                        {hasUnreadInvoices && <span className="invoices-unread-indicator" role="img" aria-label="new" />}
                      </button>
                    </li>
                    <li role="none">
                      <button type="button" role="menuitem" className="header-menu-item" onClick={handleGoToMessages}>
                        <img src="/icons/message.png" alt="" />
                        Messages
                        {hasUnreadMessages && <span className="messages-unread-indicator" role="img" aria-label="new" />}
                      </button>
                    </li>
                    {userType === 'Company' && (
                      <li role="none">
                        <button type="button" role="menuitem" className="header-menu-item" onClick={handleCalendarClick}>
                          <img src="/icons/schedule.png" alt="" />
                          Schedule
                          {hasPendingRequests && <span className="schedule-unread-indicator" role="img" aria-label="new" />}
                        </button>
                      </li>
                    )}
                    <li role="none">
                      <button type="button" role="menuitem" className="header-menu-item" onClick={handleLogout}>
                        <img src="/icons/logout.png" alt="" />
                        Log Out
                      </button>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <div className="header-auth-buttons">
              <button className="header-nav-list-button" onClick={() => navigate('/create-profile?tab=company')}>
                List Your Business
              </button>
              <button className="header-nav-sign-in-button" onClick={toggleSignInModal}>
                Log In
              </button>
            </div>
          )}
        </nav>
        {showSignInModal && <SignInModal onClose={toggleSignInModal} returnTo={returnTo} />}
      </div>
    </header>
  );
};

export default Header;